import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  HardHat, Plus, X, Loader2, Copy, Trash2, ImagePlus, Check, Hammer, Clock, ChevronLeft,
} from "lucide-react";
import { toast } from "sonner";
import { api, photoUrl } from "@/lib/api";

const NEXT_STATUS = { a_venir: "en_cours", en_cours: "termine", termine: "a_venir" };
const STATUS_META = {
  termine: { label: "Terminé", icon: Check, cls: "bg-sage/20 text-navy" },
  en_cours: { label: "En cours", icon: Hammer, cls: "bg-cognac/15 text-cognac-dark" },
  a_venir: { label: "À venir", icon: Clock, cls: "bg-slate-100 text-slate-400" },
};

const EMPTY_FORM = { client_name: "", client_email: "", model: "", address: "" };

export const AdminProjects = () => {
  const [projects, setProjects] = useState([]);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [creating, setCreating] = useState(false);
  const [caption, setCaption] = useState("");
  const [uploadPct, setUploadPct] = useState(null);
  const fileRef = useRef(null);

  const fetchProjects = useCallback(() => {
    api.get("/admin/projects")
      .then((res) => setProjects(res.data))
      .catch(() => toast.error("Impossible de charger les chantiers"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchProjects();
    api.get("/models").then((res) => setModels(res.data)).catch(() => {});
  }, [fetchProjects]);

  const refreshDetail = (id) =>
    api.get("/admin/projects").then((res) => {
      setProjects(res.data);
      setDetail(res.data.find((p) => p.id === id) || null);
    });

  const createProject = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await api.post("/admin/projects", form);
      toast.success(`Chantier créé — code client ${res.data.access_code}`);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      fetchProjects();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };

  const cycleMilestone = async (p, m) => {
    try {
      await api.patch(`/admin/projects/${p.id}/milestones/${m.id}`, { status: NEXT_STATUS[m.status] });
      refreshDetail(p.id);
    } catch {
      toast.error("Erreur lors de la mise à jour du jalon");
    }
  };

  const uploadPhoto = async (file) => {
    if (!file || !detail) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    setUploadPct(0);
    try {
      await api.post(`/admin/projects/${detail.id}/photos`, fd, {
        onUploadProgress: (e) => setUploadPct(Math.round((e.loaded * 100) / (e.total || 1))),
      });
      toast.success("Photo publiée pour le client");
      setCaption("");
      setUploadPct(null);
      refreshDetail(detail.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
      setUploadPct(null);
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    try {
      await api.delete(`/admin/projects/${detail.id}/photos/${photoId}`);
      toast.success("Photo retirée");
      refreshDetail(detail.id);
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const removeProject = async (p) => {
    if (!window.confirm(`Supprimer le chantier de ${p.client_name} ?`)) return;
    try {
      await api.delete(`/admin/projects/${p.id}`);
      toast.success("Chantier supprimé");
      setDetail(null);
      fetchProjects();
    } catch {
      toast.error("Erreur lors de la suppression");
    }
  };

  const copyLink = (p) => {
    navigator.clipboard.writeText(`${window.location.origin}/suivi/${p.access_code}`);
    toast.success("Lien client copié");
  };

  if (loading) {
    return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;
  }

  // ---- Détail d'un chantier ----
  if (detail) {
    return (
      <div data-testid="project-detail">
        <button
          data-testid="project-back"
          onClick={() => { setDetail(null); fetchProjects(); }}
          className="flex items-center gap-2 text-sm font-semibold text-slate-500 transition-colors duration-300 hover:text-cognac"
        >
          <ChevronLeft className="h-4 w-4" /> Tous les chantiers
        </button>

        <div className="mt-5 flex flex-wrap items-start justify-between gap-4 rounded-2xl border border-slate-200 bg-white p-6">
          <div>
            <h2 className="font-display text-2xl font-medium text-navy">{detail.client_name}</h2>
            <p className="mt-1 text-sm text-slate-500">
              {detail.model || "Modèle à définir"} · {detail.address || "Adresse à définir"} · {detail.client_email}
            </p>
            <div className="mt-3 flex items-center gap-3">
              <span className="rounded-full bg-navy px-3.5 py-1.5 text-xs font-bold tracking-widest text-white">
                Code : {detail.access_code}
              </span>
              <button
                data-testid="project-copy-link"
                onClick={() => copyLink(detail)}
                className="flex items-center gap-1.5 rounded-full border border-navy/15 px-3.5 py-1.5 text-xs font-bold text-navy transition-colors duration-300 hover:border-cognac hover:text-cognac"
              >
                <Copy className="h-3.5 w-3.5" /> Copier le lien client
              </button>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <p data-testid="project-progress" className="font-display text-4xl font-semibold text-cognac">{detail.progress}%</p>
            <button
              data-testid="project-delete"
              onClick={() => removeProject(detail)}
              className="rounded-full border border-red-200 p-2.5 text-red-400 transition-colors duration-300 hover:bg-red-500 hover:text-white"
              aria-label="Supprimer le chantier"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <h3 className="mt-8 font-display text-xl font-medium text-navy">Jalons</h3>
        <p className="mt-1 text-xs text-slate-400">Cliquez sur un statut pour faire avancer le jalon (À venir → En cours → Terminé).</p>
        <div className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white">
          {detail.milestones.map((m, i) => {
            const meta = STATUS_META[m.status];
            const Icon = meta.icon;
            return (
              <div key={m.id} data-testid={`milestone-row-${i}`} className="flex items-center justify-between gap-4 border-b border-slate-100 px-5 py-3.5 last:border-0">
                <p className={`text-sm font-semibold ${m.status === "a_venir" ? "text-slate-400" : "text-navy"}`}>
                  {m.title}
                  {m.date && <span className="ml-2 text-xs font-normal text-slate-400">{m.date}</span>}
                </p>
                <button
                  data-testid={`milestone-toggle-${i}`}
                  onClick={() => cycleMilestone(detail, m)}
                  className={`flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-transform duration-200 hover:scale-105 ${meta.cls}`}
                >
                  <Icon className="h-3.5 w-3.5" /> {meta.label}
                </button>
              </div>
            );
          })}
        </div>

        <h3 className="mt-8 font-display text-xl font-medium text-navy">Photos d'avancement</h3>
        <div className="mt-4 rounded-2xl border border-slate-200 bg-white p-5">
          <div className="flex flex-wrap items-center gap-3">
            <input
              data-testid="photo-caption-input"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Légende (ex. Fondations coulées)"
              className="min-w-52 flex-1 rounded-full border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none"
            />
            <input
              ref={fileRef}
              data-testid="photo-upload-input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => uploadPhoto(e.target.files?.[0])}
            />
            <button
              data-testid="photo-upload-button"
              onClick={() => fileRef.current?.click()}
              disabled={uploadPct !== null}
              className="flex items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-cognac disabled:opacity-60"
            >
              {uploadPct !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
              {uploadPct !== null ? `Envoi ${uploadPct}%` : "Ajouter une photo"}
            </button>
          </div>
          {uploadPct !== null && (
            <div data-testid="photo-upload-progress" className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100">
              <div className="h-full bg-cognac transition-all" style={{ width: `${uploadPct}%` }} />
            </div>
          )}
          {detail.photos.length > 0 ? (
            <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3">
              {detail.photos.map((ph) => (
                <figure key={ph.id} data-testid={`admin-photo-${ph.id}`} className="group relative overflow-hidden rounded-xl">
                  <img src={photoUrl(ph)} alt={ph.caption || "Photo"} className="h-36 w-full object-cover" />
                  {ph.caption && (
                    <figcaption className="absolute inset-x-0 bottom-0 bg-navy/70 px-3 py-1.5 text-xs font-semibold text-white">
                      {ph.caption}
                    </figcaption>
                  )}
                  <button
                    data-testid={`photo-delete-${ph.id}`}
                    onClick={() => removePhoto(ph.id)}
                    className="absolute right-2 top-2 rounded-full bg-white/90 p-1.5 text-red-500 opacity-0 transition-opacity duration-200 group-hover:opacity-100"
                    aria-label="Supprimer la photo"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </figure>
              ))}
            </div>
          ) : (
            <p data-testid="photos-empty" className="mt-4 text-sm text-slate-400">Aucune photo publiée pour le moment.</p>
          )}
        </div>
      </div>
    );
  }

  // ---- Liste des chantiers ----
  return (
    <div data-testid="projects-list">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Créez un chantier par client : jalons, photos d'avancement et lien de suivi.
        </p>
        <button
          data-testid="project-create-button"
          onClick={() => setShowCreate(true)}
          className="flex shrink-0 items-center gap-2 rounded-full bg-cognac px-5 py-2.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-navy"
        >
          <Plus className="h-4 w-4" /> Nouveau chantier
        </button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {projects.length === 0 ? (
          <p data-testid="projects-empty" className="py-16 text-center text-sm text-slate-400">
            Aucun chantier pour le moment.
          </p>
        ) : (
          projects.map((p) => (
            <div
              key={p.id}
              data-testid={`project-row-${p.id}`}
              onClick={() => setDetail(p)}
              className="flex cursor-pointer flex-wrap items-center gap-4 border-b border-slate-100 px-5 py-4 transition-colors duration-200 last:border-0 hover:bg-cream"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy/5 text-navy">
                <HardHat className="h-5 w-5" />
              </span>
              <div className="min-w-40 flex-1">
                <p className="font-bold text-navy">{p.client_name}</p>
                <p className="text-xs text-slate-400">{p.model || "—"} · {p.address || "—"}</p>
              </div>
              <div className="w-32">
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-cognac" style={{ width: `${p.progress}%` }} />
                </div>
                <p className="mt-1 text-xs font-bold text-slate-400">{p.progress}% · code {p.access_code}</p>
              </div>
            </div>
          ))
        )}
      </div>

      <AnimatePresence>
        {showCreate && (
          <motion.div
            data-testid="project-create-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-navy/70 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={() => setShowCreate(false)}
          >
            <motion.form
              onSubmit={createProject}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md rounded-t-3xl bg-white p-7 sm:rounded-3xl sm:p-9"
            >
              <div className="flex items-start justify-between">
                <h2 className="font-display text-2xl font-medium text-navy">Nouveau chantier</h2>
                <button
                  type="button"
                  data-testid="project-create-close"
                  onClick={() => setShowCreate(false)}
                  className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors duration-300 hover:bg-navy hover:text-white"
                  aria-label="Fermer"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="mt-6 space-y-4">
                <input
                  data-testid="project-form-name"
                  required
                  value={form.client_name}
                  onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                  placeholder="Nom du client (ex. Marie & Thomas Bernard)"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cognac focus:outline-none"
                />
                <input
                  data-testid="project-form-email"
                  required
                  type="email"
                  value={form.client_email}
                  onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                  placeholder="Email du client"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cognac focus:outline-none"
                />
                <select
                  data-testid="project-form-model"
                  value={form.model}
                  onChange={(e) => setForm({ ...form, model: e.target.value })}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-cognac focus:outline-none"
                >
                  <option value="">Modèle de maison (optionnel)</option>
                  {models.map((m) => (
                    <option key={m.slug} value={m.name}>{m.name} — {m.surface} m²</option>
                  ))}
                </select>
                <input
                  data-testid="project-form-address"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  placeholder="Adresse du chantier (ex. 12 rue des Tilleuls, Vesoul)"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm focus:border-cognac focus:outline-none"
                />
              </div>
              <button
                data-testid="project-form-submit"
                type="submit"
                disabled={creating}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-navy px-6 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-cognac disabled:opacity-60"
              >
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Créer le chantier
              </button>
              <p className="mt-3 text-center text-xs text-slate-400">
                Un code d'accès client est généré automatiquement (ex. DEMO2026).
              </p>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
